import { TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { distinctLanguages } from './entryFilters';

import type { TranslationMemoryEntry } from '@src/repositories/TranslationMemoryEntryRepository';

type Status = 'idle' | 'loading' | 'ready' | 'error';

// Cell key separator + the synthetic column id for empty-target ("all
// languages") entries — a NUL byte can't collide with a real term or language id.
const SEP = '\u001f';
const ALL_LANGUAGES = `${SEP}all${SEP}`;

const MATRIX_TOOLTIP_ID = 'tm-matrix-cell';
// Multi-line, left-aligned tooltip listing each contributing collection.
const CELL_TOOLTIP_STYLE = {
  ...TOOLTIP_STYLE,
  maxWidth: 280,
  whiteSpace: 'pre-line' as const,
  textAlign: 'left' as const,
};

/** One entry landing in a cell: its translation and the collection it came from. */
interface CellDatum {
  translation: string;
  collectionName: string;
}

interface Props {
  /** Entries already scoped to the selected collection, the search query, and
   *  the chosen base (source) language. */
  entries: TranslationMemoryEntry[];
  /** Caption of the base language, shown as the first column header. */
  baseLanguageName: string;
  /** Label of the selected collection ("Default" / "All translations" / name). */
  collectionName: string;
  /** Resolves an entry's collection id (null = Default) to a display name — the
   *  provenance shown in a cell's "Multiple" tooltip. */
  resolveCollectionName: (collectionId: string | null) => string;
  status: Status;
  error: string | null;
  retry: () => void;
}

/**
 * The pivot / matrix view (PLAT-10847, E16): rows are the distinct source terms
 * in the chosen base language, columns are each target language present, and
 * each cell is that term's translation for that language (blank where none).
 * Entries with an empty target-language link (the "all languages" / DNT
 * convention, E4) get a dedicated leading column rather than being smeared
 * across every target column. When a cell is backed by entries from more than
 * one collection, a "Multiple" indicator lists them on hover. Read-only —
 * curation lives in the table view.
 */
const MatrixView = ({
  entries,
  baseLanguageName,
  collectionName,
  resolveCollectionName,
  status,
  error,
  retry,
}: Props) => {
  // Rows: distinct source terms (case-insensitive), first-seen casing kept.
  const termByKey = new Map<string, string>();
  for (const e of entries) {
    const key = e.sourceTerm.toLowerCase();
    if (!termByKey.has(key)) termByKey.set(key, e.sourceTerm);
  }
  const terms = [...termByKey.entries()].sort((a, b) =>
    a[1].localeCompare(b[1]),
  );

  // Columns: distinct present target languages, plus a leading "All languages"
  // column only when some entry applies to every locale (empty target link).
  const targetColumns = distinctLanguages(
    entries.map(e => e.translationLanguage),
  );
  const hasAllLanguages = entries.some(e => e.translationLanguage == null);

  // Cell lookup: (lower term × target column) → each contributing entry, with
  // its collection for provenance.
  const cells = new Map<string, CellDatum[]>();
  for (const e of entries) {
    const colId = e.translationLanguage?.id ?? ALL_LANGUAGES;
    const key = `${e.sourceTerm.toLowerCase()}${SEP}${colId}`;
    const datum: CellDatum = {
      translation: e.translation,
      collectionName: resolveCollectionName(e.collectionId),
    };
    const bucket = cells.get(key);
    if (bucket) bucket.push(datum);
    else cells.set(key, [datum]);
  }

  const columnCount = 1 + (hasAllLanguages ? 1 : 0) + targetColumns.length;
  const hasRows = terms.length > 0;

  const cellFor = (termKey: string, colId: string) =>
    cells.get(`${termKey}${SEP}${colId}`);

  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <th className="term-col">{baseLanguageName || 'Source term'}</th>
            {hasAllLanguages && <th>All languages</th>}
            {targetColumns.map(l => (
              <th key={l.id}>{l.caption ?? l.value ?? l.id}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hasRows ? (
            terms.map(([key, display]) => (
              <tr key={key}>
                <td className="term-col">{display}</td>
                {hasAllLanguages && <Cell data={cellFor(key, ALL_LANGUAGES)} />}
                {targetColumns.map(l => (
                  <Cell key={l.id} data={cellFor(key, l.id)} />
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <EmptyCell colSpan={Math.max(columnCount, 1)}>
                {status === 'loading' && 'Loading entries…'}
                {status === 'ready' &&
                  `No entries in ${collectionName} yet. Add one to start building your glossary.`}
                {status === 'error' && (
                  <span role="alert">
                    {error}{' '}
                    <button type="button" onClick={retry}>
                      Retry
                    </button>
                  </span>
                )}
              </EmptyCell>
            </tr>
          )}
        </tbody>
      </Table>
      <Tooltip id={MATRIX_TOOLTIP_ID} style={CELL_TOOLTIP_STYLE} place="top" />
    </TableScroll>
  );
};

export default MatrixView;

// One matrix cell: the distinct translation(s) for the term/language (identical
// values collapse), plus a "Multiple" indicator — hover to see which
// collections contributed — when more than one entry lands here. A muted dash
// when the term has no translation for this language.
const Cell = ({ data }: { data: CellDatum[] | undefined }) => {
  if (!data || data.length === 0) return <td className="blank">—</td>;
  const distinct = [...new Set(data.map(d => d.translation))];
  // Collection name over its translation, entries divided by a rule.
  const tooltip = data
    .map(d => `${d.collectionName}\n${d.translation}`)
    .join('\n─────\n');
  return (
    <td>
      {distinct.map((v, i) => (
        <span key={`${v}-${i}`} className="value">
          {v}
        </span>
      ))}
      {data.length > 1 && (
        <MultipleBadge
          data-tooltip-id={MATRIX_TOOLTIP_ID}
          data-tooltip-content={tooltip}
        >
          Multiple
        </MultipleBadge>
      )}
    </td>
  );
};

const TableScroll = styled.div`
  overflow: auto;
  max-height: 55vh;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  > thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--background);
    text-align: left;
    padding: 8px 12px;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semi-bold);
    color: var(--text-muted);
    /* box-shadow (not border-bottom) so the divider stays attached to the
       sticky header — a collapsed border scrolls away with the body. */
    box-shadow: inset 0 -1px 0 var(--border);
    white-space: nowrap;
  }

  /* The term column stays pinned to the left while target columns scroll. */
  .term-col {
    position: sticky;
    left: 0;
    background: var(--background);
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
    overflow-wrap: anywhere;
  }

  > thead th.term-col {
    z-index: 3;
  }

  > tbody tr {
    border-bottom: 1px solid var(--border);
  }

  > tbody tr:last-child {
    border-bottom: none;
  }

  > tbody td {
    padding: 8px 12px;
    font-size: var(--text-xs);
    color: var(--foreground);
    vertical-align: top;
    min-width: 120px;
  }

  td.blank {
    color: var(--text-muted);
  }

  td .value {
    display: block;
    overflow-wrap: anywhere;
  }
`;

// A subtle pill flagging that the cell is backed by entries from more than one
// collection; the collections (and their translations) show on hover.
const MultipleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 3px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 9px;
  line-height: 1.5;
  color: var(--text-muted);
  background: var(--surface-elevated);
  cursor: help;
`;

const EmptyCell = styled.td`
  padding: 32px 12px;
  text-align: center;
  font-size: var(--text-sm);
  color: var(--text-secondary);

  button {
    background: none;
    border: none;
    color: var(--primary);
    cursor: pointer;
    font-size: inherit;
    padding: 0;
  }
`;
